import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import {
  type FileSystemNode,
  selectedDirectoriesToPatterns,
  selectedItemsToFileIds,
} from "@/shared/domain/files/filesystem";
import {
  prepareSessionComposition,
  reconcileSessionComposition,
  type SessionCompositionOutcome,
  type SessionCompositionRetrySelection,
  type SessionRecord,
} from "@/shared/domain/sessions";
import { useAddFilesToSession } from "@/shared/domain/sessions/hooks";
import { userFacingErrorMessage } from "@/shared/http";

interface ControllerOptions {
  orgId: string | undefined;
  datasetId: string;
  selectedItems: FileSystemNode[];
}

/** The file-browser selection frozen at the moment the dialog opened. An addition sends these
 * files, never the live selection, so picking different files while it is under way cannot change
 * what is sent. */
interface SelectionSnapshot {
  operationId: string;
  orgId: string;
  datasetId: string;
  fileIds: readonly string[];
  directoryPatterns: readonly string[];
  /** Compared against the live file-browser selection to detect that it changed. */
  selectionKey: string;
}

type Phase = "idle" | "enumerating" | "submitting" | "reconciling";

interface ViewState {
  /** Present while the dialog is open. */
  snapshot?: SelectionSnapshot;
  selectedSession: SessionRecord | null;
  /** What happened to each file, present once an addition has finished sending. Its presence is
   * what moves the dialog from choosing a session to showing results. */
  outcome?: SessionCompositionOutcome;
  error?: React.ReactNode;
  phase: Phase;
  /** The user canceled while a request was out: no further batch is sent, and the dialog waits for
   * that request so it can show what the session did with it. */
  stopping: boolean;
}

const emptyView: ViewState = {
  selectedSession: null,
  phase: "idle",
  stopping: false,
};

/** Identity of a selection: the file ids and directory patterns it resolves to, order-independent. */
function selectionKey(items: FileSystemNode[]) {
  return JSON.stringify([
    selectedItemsToFileIds(items).sort(),
    selectedDirectoriesToPatterns(items).sort(),
  ]);
}

/** Own an addition of the file-browser selection to an existing session, from choosing the session
 * through the per-file results. Closing the dialog forgets the addition, so the next one starts
 * from the files selected at that moment. */
export function useAddToSessionController(options: ControllerOptions) {
  const { datasetService, sessionService } = useDomainServices();
  const mutation = useAddFilesToSession();
  const [view, setView] = React.useState<ViewState>(emptyView);
  const latestView = React.useRef(view);
  const latestOptions = React.useRef(options);
  latestOptions.current = options;
  const mounted = React.useRef(true);
  const currentSelectionKey = selectionKey(options.selectedItems);

  React.useEffect(() => {
    mounted.current = true;
    // Once the component using this hook unmounts, `stopRequested` holds back every later batch.
    // The request already out still completes, with no dialog left to show its result.
    return () => {
      mounted.current = false;
    };
  }, []);

  const commit = (next: ViewState) => {
    latestView.current = next;
    setView(next);
  };

  /** Change the view only while `snapshot` is still the addition on screen, so a request that
   * outlives its dialog cannot change a dialog opened since. */
  const update = (snapshot: SelectionSnapshot, changes: Partial<ViewState>) => {
    if (latestView.current.snapshot === snapshot) {
      commit({ ...latestView.current, ...changes });
    }
  };

  const matchesContext = (snapshot: SelectionSnapshot) =>
    latestOptions.current.orgId === snapshot.orgId &&
    latestOptions.current.datasetId === snapshot.datasetId;

  /** Whether the addition of `snapshot` must send no further batch:
   * 1. the user canceled while a request was out,
   * 2. the component using this hook unmounted,
   * 3. the dialog closed, or shows a later addition,
   * 4. the user moved to another org or dataset. */
  const stopRequested = (snapshot: SelectionSnapshot) =>
    latestView.current.stopping ||
    !mounted.current ||
    latestView.current.snapshot !== snapshot ||
    !matchesContext(snapshot);

  const selectionUnchanged = (snapshot: SelectionSnapshot) =>
    selectionKey(latestOptions.current.selectedItems) === snapshot.selectionKey;

  const show = () => {
    const { orgId, datasetId, selectedItems } = latestOptions.current;
    if (orgId === undefined) {
      return;
    }
    const snapshot: SelectionSnapshot = Object.freeze({
      operationId: crypto.randomUUID(),
      orgId,
      datasetId,
      fileIds: Object.freeze(selectedItemsToFileIds(selectedItems)),
      directoryPatterns: Object.freeze(
        selectedDirectoriesToPatterns(selectedItems),
      ),
      selectionKey: selectionKey(selectedItems),
    });
    commit({ ...emptyView, snapshot });
  };

  /** Runs for Cancel, Done, Close, and dismissing the dialog with Escape or a click outside it.
   * 1. While files are being sent: the request already out cannot be recalled, so no later batch
   *    is sent and the dialog stays to show what the session did with the files already sent.
   * 2. Otherwise: closes the dialog and forgets the addition. A check of unconfirmed files that is
   *    still reading the session has only read, so there is nothing to wait for. */
  const close = () => {
    const { phase } = latestView.current;
    commit(
      phase === "submitting"
        ? { ...latestView.current, stopping: true }
        : emptyView,
    );
  };

  const selectSession = (session: SessionRecord | null) => {
    if (latestView.current.phase !== "idle" || latestView.current.outcome) {
      return;
    }
    commit({
      ...latestView.current,
      selectedSession: session ? Object.freeze({ ...session }) : null,
      error: undefined,
    });
  };

  /** Merge the snapshot's explicitly picked files with every file under its directory patterns.
   * Returns undefined when the addition must not proceed: a stop was requested, or the selection
   * moved on while the listing was in flight. */
  const resolveSnapshotFileIds = async (snapshot: SelectionSnapshot) => {
    const directoryFiles =
      snapshot.directoryPatterns.length > 0
        ? await datasetService.listAllFiles(snapshot.datasetId, {
            includePatterns: [...snapshot.directoryPatterns],
          })
        : [];
    if (stopRequested(snapshot) || !selectionUnchanged(snapshot)) {
      return undefined;
    }
    return new Set([
      ...snapshot.fileIds,
      ...directoryFiles.map((file) => file.file_id),
    ]);
  };

  const submit = async (retrySelection?: SessionCompositionRetrySelection) => {
    const start = latestView.current;
    const { snapshot, selectedSession } = start;
    if (
      !snapshot ||
      !selectedSession ||
      start.phase !== "idle" ||
      !matchesContext(snapshot)
    ) {
      return;
    }
    if (start.outcome && retrySelection === undefined) {
      return;
    }
    if (!start.outcome && !selectionUnchanged(snapshot)) {
      return;
    }
    update(snapshot, {
      phase: start.outcome ? "submitting" : "enumerating",
      error: undefined,
    });
    try {
      let outcome = start.outcome;
      if (outcome === undefined) {
        const fileIds = await resolveSnapshotFileIds(snapshot);
        if (fileIds === undefined) {
          return;
        }
        if (fileIds.size === 0) {
          update(snapshot, {
            error: "No files matched the current selection.",
          });
          return;
        }
        outcome = prepareSessionComposition(
          {
            operationId: snapshot.operationId,
            orgId: snapshot.orgId,
          },
          [...fileIds].map((file_id) => ({ file_id })),
          selectedSession.session_id,
        );
      }
      update(snapshot, { phase: "submitting" });
      const result = await mutation.mutateAsync({
        outcome,
        retrySelection,
        stopRequested: () => stopRequested(snapshot),
        // A retry starts from results already on screen and keeps them current batch by batch. The
        // first addition shows results only once it has finished sending.
        onProgress: start.outcome
          ? (progress) => update(snapshot, { outcome: progress })
          : undefined,
      });
      update(snapshot, { outcome: result });
    } catch (error) {
      // Add-files requests report their failures on the outcome rather than throwing, so what lands
      // here is a failed directory listing, or a selection or retry refused before anything was sent.
      update(snapshot, {
        error: userFacingErrorMessage(
          error,
          "Failed to add files to the session. Please retry.",
        ),
      });
    } finally {
      update(snapshot, { phase: "idle", stopping: false });
    }
  };

  const reconcile = async () => {
    const { snapshot, outcome, phase } = latestView.current;
    if (
      !snapshot ||
      !outcome ||
      phase !== "idle" ||
      !matchesContext(snapshot)
    ) {
      return;
    }
    update(snapshot, { phase: "reconciling", error: undefined });
    try {
      // No catch: a failed read comes back as `reconciliationError` on the outcome, leaving every
      // unknown entry unknown, so there is nothing here to swallow.
      update(snapshot, {
        outcome: await reconcileSessionComposition(sessionService, outcome),
      });
    } finally {
      update(snapshot, { phase: "idle" });
    }
  };

  return {
    ...view,
    contextMatches:
      view.snapshot === undefined || matchesContext(view.snapshot),
    selectionMatches:
      view.snapshot === undefined ||
      view.snapshot.selectionKey === currentSelectionKey,
    show,
    close,
    selectSession,
    submit: () => submit(),
    retryRemaining: () => submit({ kind: "remaining" }),
    retryUnknown: (originalIndices: readonly number[]) =>
      submit({ kind: "unknown", originalIndices }),
    replaceConflicts: (originalIndices: readonly number[]) =>
      submit({ kind: "replaceConflicts", originalIndices }),
    reconcile,
  };
}

export type AddToSessionController = ReturnType<
  typeof useAddToSessionController
>;
